import { apiFetch } from './client';

// ============================================================
// Sessions API client - Fase 3.
// Mirror de los endpoints /api/sessions/* del backend.
// ============================================================

export interface SessionSummary {
  sessionId: string;
  candleSessionId: string;
  symbol: string;
  startTime: string | number | null;
  config: Record<string, unknown> | null;
  accountName: string | null;
  customTitle: string | null;
  operatorId: string;
  dailyStartTime: string | null;
  dailyEndTime: string | null;
  developerMonitor: boolean;
  configSource: string | null;
  timeSlots: unknown[] | null;
  useOrphanMode: boolean;
  vNextRestored: boolean;
  isLive: boolean;
  hasSessionEnd: boolean;
  performanceStats: Record<string, unknown> | null;
  slotStats: unknown | null;
  slotTransitions: unknown | null;
  tradeCount: number;
  totalPnL: number;
  // BT total (sin slippage). Stacked bajo totalPnL en cards.
  totalBtPnL: number;
  wins: number;
  winRate: number;
  openPositions: number;
  winAmount: number;
  lossAmount: number;
  // null = infinito (no hay losses); UI lo muestra como '∞' o '—'.
  profitFactor: number | null;

  // Config expandido para badges + tooltip de symbol.
  timeSpan: string | null;
  slippage: number;
  offset: number;
  atrPeriod: number | null;
  boxSizeMultiplier: number | null;
  // Mode badges - reglas en docs/RH_DashBoard/sessions-tab-badges.md
  isTimeSlotSession: boolean; // TS naranja
  filterWarmupToTradingHours: boolean; // WPM verde
  dwpmActive: boolean;
  dwpmLabel: string | null; // 'Auto' | numero como string
  warmupSessions: number;
  enableBracketReset: boolean; // BSR verde
  // PX5 cyan (Zap + pulse): config.exitMode === 'polling'.
  pollingExit: boolean;
  accountKind: AccountKind;
}

/**
 * Categoria de la cuenta:
 *   - live  : real money (Live Main, Live 4954, config.mode='live')
 *   - paper : paper trading (config.mode='paper')
 *   - sim   : simulacion (Sim XXX, config.mode='sim')
 *   - demo  : demo (Demo Main)
 *   - unknown: no matchea ninguna regla
 *
 * Logica de derivacion en backend (services/sessions.ts).
 */
export type AccountKind = 'live' | 'paper' | 'sim' | 'demo' | 'unknown';

export type ParityStatus = 'OK' | 'DRIFT' | 'CRITICAL' | string;

export interface ParityStatusEntry {
  sessionId: string;
  status: ParityStatus;
}

export async function fetchSessionSummaries(): Promise<SessionSummary[]> {
  const res = await apiFetch<PaginatedSessions>('/api/sessions/summaries');
  return res.sessions;
}

export type SessionsSortColumn =
  | 'sessionId'
  | 'symbol'
  | 'accountName'
  | 'operatorId'
  | 'startTime'
  | 'totalPnL'
  | 'tradeCount'
  | 'winRate'
  | 'profitFactor';
export type SessionsSortDir = 'asc' | 'desc';
export type AccountKindFilter = 'all' | AccountKind;
export type StatusFilter = 'all' | 'live' | 'closed' | 'active';

export interface SessionsQueryParams {
  page?: number;
  pageSize?: number;
  sort?: SessionsSortColumn;
  dir?: SessionsSortDir;
  kind?: AccountKindFilter;
  status?: StatusFilter;
  q?: string;
  // ----- nuevos filtros (port v1 dropdowns) -----
  // dateFrom/dateTo: ISO YYYY-MM-DD (ET trading day). null = sin filtro.
  dateFrom?: string;
  dateTo?: string;
  // accounts/titles: lista de valores exactos. '__null__' incluye sin valor.
  accounts?: string[];
  titles?: string[];
}

export interface PaginatedSessions {
  sessions: SessionSummary[];
  page: number;
  pageSize: number;
  total: number;
  pages: number;
  // ids del set filtrado completo (no solo la pagina), usado por el
  // Portfolio Overview para correr aggregations sobre todo el set.
  allSessionIds: string[];
}

/**
 * Fetch paginado con filtros server-side. Devuelve la pagina actual
 * + total + ids del set completo (para portfolio).
 */
export async function fetchSessionsPaginated(
  params: SessionsQueryParams = {},
): Promise<PaginatedSessions> {
  const qs = new URLSearchParams();
  if (params.page !== undefined) qs.set('page', String(params.page));
  if (params.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
  if (params.sort) qs.set('sort', params.sort);
  if (params.dir) qs.set('dir', params.dir);
  if (params.kind && params.kind !== 'all') qs.set('kind', params.kind);
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.dateFrom) qs.set('dateFrom', params.dateFrom);
  if (params.dateTo) qs.set('dateTo', params.dateTo);
  if (params.accounts && params.accounts.length > 0) qs.set('accounts', params.accounts.join(','));
  if (params.titles && params.titles.length > 0) qs.set('titles', params.titles.join(','));
  const qsStr = qs.toString();
  return await apiFetch<PaginatedSessions>(`/api/sessions/summaries${qsStr ? `?${qsStr}` : ''}`);
}

export interface SessionFilterOptions {
  accounts: string[];
  titles: string[];
}

/**
 * Fetch los valores únicos de accountName y customTitle para los dropdowns
 * multi-select del tab Sessions. accountName con '__null__' representa
 * docs sin cuenta — el frontend renderiza como "(no account)".
 */
export async function fetchSessionFilterOptions(): Promise<SessionFilterOptions> {
  return await apiFetch<SessionFilterOptions>('/api/sessions/filter-options');
}

export async function fetchSessionIds(): Promise<string[]> {
  const res = await apiFetch<{ sessionIds: string[] }>('/api/sessions/ids');
  return res.sessionIds;
}

export async function patchSessionTitle(sessionId: string, customTitle: string): Promise<void> {
  await apiFetch<void>(`/api/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ customTitle }),
  });
}

/**
 * Fetch sessionIds que tienen al menos un renkoBlock. Usado para
 * decidir el icono del boton Renko (📊 existe vs 🔨 crear).
 */
export async function fetchRenkoSessionIds(): Promise<string[]> {
  const res = await apiFetch<{ sessionIds: string[] }>('/api/renko/sessions');
  return res.sessionIds;
}

export interface EquityCurvePoint {
  exitTime: string; // ISO UTC
  cumulativePnL: number;
}

export interface PortfolioStats {
  totalTrades: number;
  totalProfit: number;
  totalWins: number;
  totalLosses: number;
  winRate: number;
  avgProfit: number;
  avgWin: number;
  avgLoss: number;
  maxProfit: number;
  maxLoss: number;
  totalWinAmount: number;
  totalLossAmount: number;
  profitFactor: number | null;
  maxDrawdown: number; // valor negativo o 0
  firstTradeTime: string | null;
  lastTradeTime: string | null;
  equityCurve: EquityCurvePoint[];
}

/**
 * Stats globales del portfolio. Si sessionIds se pasa, filtra; si no,
 * stats sobre todos los closed trades.
 */
export async function fetchPortfolioStats(sessionIds?: string[]): Promise<PortfolioStats> {
  const qs =
    sessionIds && sessionIds.length > 0
      ? `?sessionIds=${encodeURIComponent(sessionIds.join(','))}`
      : '';
  const res = await apiFetch<{ stats: PortfolioStats }>(`/api/sessions/portfolio-stats${qs}`);
  return res.stats;
}

/**
 * Fetch los warmup parity statuses por sessionId. Usado para los
 * badges WP:OK / WP:DRIFT / WP:CRITICAL en la tabla.
 */
export async function fetchParityStatuses(): Promise<ParityStatusEntry[]> {
  const res = await apiFetch<{ statuses: ParityStatusEntry[] }>('/api/parity/statuses');
  return res.statuses;
}
